import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import {
  type Control,
  type FieldErrors,
  type FieldValues,
  type UseFormReset,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form'
import { Tooltip } from 'antd'

import Checkbox from 'components/shared/inputs/checkbox'
import Input, { InputType } from 'components/shared/inputs/input'
import {
  useGetLossMutation,
  useLazyGetTbaseQuery,
  useGetFlowTemperatureMutation,
  useGetParamentersQuery,
  useLazyGetDepartmentsQuery,
  useLazyGetStationsQuery,
} from 'store/api'
import { SvgInfo, SvgInfoOutline } from 'components/svg'
import { convertToSelect } from 'utils'
import { useSalesNetwork } from 'utils/hooks/useSalesNetworks'
import useTranslation from 'utils/hooks/useTranslation'
import HeatModeZone from './HeatModeZone'
import * as ddLoss from './dropDownLossMethods.data'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { HelpSidePopup } from 'components/popups/HelpSidePopup'
import { usePopup } from 'providers/PopupProvider'
import { env } from 'env'
import CustomSelector from '../CustomSelector'
import _ from 'lodash'
import { EMETTEUR_CODES, houseTypes, temperature } from 'constants/dropdown'
import { MITOYENNETE_ITEMS, LEVEL_ITEMS } from 'constants/heatModeInformation'
import { updateForm, setNewTransmitterTemp, setDeperds, setFlowTemperature, mergeFlowTemperature } from 'store/projectSlice'
import { updateTbase, load as loadHeatModeAction, addItem as addHeatModeItem, defaultZone, heatModeSchema, defaultForm } from 'store/heatModeSlice'
import { LinkButton } from 'components/LinkButton'
import HeatModeCalculatedNeeds from './HeatModeCalculatedNeeds'

interface HeatModeProps {
  control: Control<any>
  watch: UseFormWatch<any>
  setValue: UseFormSetValue<any>
  heatModeformStore: any
  reset: UseFormReset<any>
  getValues: any
  setDisableNavigarion?: any
  getFieldState?: any
  errors: FieldErrors<any>
  onSelect: (name: keyof FieldValues, code?: string | null, label?: string | null, shouldValidate?: boolean, shouldDirty?: boolean) => void
  trigger?: any
}

interface DeperdComponentProps {
  deperditions?: any
  errors: FieldErrors<any>
  touched?: any
  onChange?: (e: any) => void
  onBlur?: (e: any) => void
  calculateDeperd?: () => void
  control?: Control<any>
  watch?: UseFormWatch<any>
  addZone?: () => void
  removeZone?: (index: number) => void
  setValue?: UseFormSetValue<any>
  getFieldState?: any
  onSelect?: (name: keyof FieldValues, code?: string | null, label?: string | null, shouldValidate?: boolean, shouldDirty?: boolean) => void
  getValues?: any
  setDisableNavigarion?: any
  trigger?: any
  calculateDeperdAndFlowTemperature?: () => void
}

const HeatMode: React.FC<HeatModeProps> = ({
  control,
  watch,
  setValue,
  heatModeformStore: heatModeformStore,
  getValues,
  setDisableNavigarion,
  getFieldState,
  errors,
  onSelect,
  trigger,
}) => {
  const { t, tHtml } = useTranslation()
  const dispatch = useAppDispatch()
  const { network, isThermor } = useSalesNetwork()

  const [data, setData] = useState<{
    departments: any[]
    stations: any[]
    deperditionMethods: any[]
    DeperdComponent: React.ComponentType<DeperdComponentProps> | null
  }>({
    departments: [],
    stations: [],
    deperditionMethods: ddLoss.getLossMethods(network?._parameters?.lossMethods),
    DeperdComponent: ddLoss.getLossMethods(network?._parameters?.lossMethods)[0].component as React.ComponentType<DeperdComponentProps>,
  })

  let debounced: any = null
  let deperdsLoading: any = null

  const { openPopup, closeLastPopup } = usePopup()

  const projectStore = useAppSelector((state) => state.project)
  const heatModeForm = projectStore.project.forms.heatModeStep?.form

  const [departement, stationMeteo, altitude, seaside, tbase, mitoyennete, level, zones, deperditions] = watch([
    'heatMode.departement',
    'heatMode.stationMeteo',
    'heatMode.altitude',
    'heatMode.seaside',
    'heatMode.tbase',
    'heatMode.mitoyennete',
    'heatMode.level',
    'heatMode.zones',
    'heatMode.deperditions',
  ])

  const [getDepartments, { isFetching: isFetchingDepartments }] = useLazyGetDepartmentsQuery()

  const [getStations, { isFetching: isFetchingStations }] = useLazyGetStationsQuery()

  const [getLoss, { isLoading, data: lossData, error }] = useGetLossMutation()
  const [getFlowTemp] = useGetFlowTemperatureMutation()

  const [triggerGetTbase] = useLazyGetTbaseQuery()

  const bordDeMer = network?._parameters.bordDeMer;
  const desiredTemp = network?._parameters.desiredTemp;
  const zone3Enabled = network?._parameters.zone3;
  const hasSavedZone3 = (zones?.length ?? 0) >= 3 || (heatModeForm?.zones?.length ?? 0) >= 3;
  const maxHeatModeZones = zone3Enabled || hasSavedZone3 ? 3 : 2;

  useEffect(() => {
    async function fetchInitialData() {
      if (projectStore.project.forms.heatModeStep?.form != null) {
        setValue('heatMode', projectStore.project.forms.heatModeStep.form)
      }


      const departments =
        heatModeForm?.departmentsList && heatModeForm?.departmentsList.length
          ? heatModeForm?.departmentsList
          : network != undefined && (await getDepartments({ env: network?.code }).unwrap())
      setValue('heatMode.departmentsList', departments || [])

      setData((prev) => ({
        ...prev,
        departments: departments || [],
        stations: heatModeForm?.stationsList || [],
      }))
    }

    fetchInitialData()
  }, [])

  useEffect(() => {
    if (!deperditions.temperature && getFieldState('heatMode.deperditions.temperature').isDirty === false) {
      if (desiredTemp) {
        setValue('heatMode.deperditions.temperature', +desiredTemp);
      } else {
        setValue('heatMode.deperditions.temperature', 19);
      }
    }
  }, [desiredTemp])

  useEffect(() => {
    if (!network?._parameters.TRANSMITTER_CALCUL) {
      zones.forEach((zone: any, index: number) => {
        if (zone.emetteur === '5') {

          onSelect(`heatMode.zones[${index}].emetteur`, null, null, false, false)
          setValue(`heatMode.zones[${index}].temperature`, null)
        }
        dispatch(mergeFlowTemperature({ index, data: null }))
      })
    }
  }, [network?._parameters.TRANSMITTER_CALCUL, zones, dispatch, setValue]);

  useEffect(() => {
    async function fetchStationsData() {
      const dataStations = await getStations({ env: network?.code, department: departement }).unwrap()
      setValue('heatMode.stationsList', dataStations)
      setData((prev) => ({
        ...prev,
        stations: dataStations,
      }))
    }
    if (departement) {
      fetchStationsData()
    }
  }, [departement])

  //if department is dirty, reset stationMeteo
  useEffect(() => {
    if (departement && getFieldState('heatMode.departement').isDirty) {
      setValue('heatMode.stationMeteo', undefined)
      setValue('heatMode.stationMeteo_value', '')
    }
  }, [departement, setValue])

  useEffect(() => {
    if (!deperditions)
      onSelect?.(
        'heatMode.deperditions.deperditionMethod',
        ddLoss.getLossMethods(network?._parameters?.lossMethods)[0].code,
        ddLoss.getLossMethods(network?._parameters?.lossMethods)[0].label as string,
      )
  }, [])

  useEffect(() => {
    // dispatch(addHeatModeItem({ name: 'stationMeteo', value: stationMeteo }))
    if (stationMeteo && data.stations.length > 0) {
      const selectedStation = data.stations.find((s) => s.code === stationMeteo)
      if (selectedStation) {
        if (selectedStation && selectedStation.altitude !== undefined) {
          setValue('heatMode.altitude', selectedStation.altitude, { shouldValidate: true })
          // dispatch(addHeatModeItem({ name: 'altitude', value: selectedStation.altitude }))
        }
        // if (tbase !== undefined) {
        //     setValue("tbase", tbase);
        // }
        calculateDeperd()
        getTbase(selectedStation.altitude)
      }
    }
  }, [stationMeteo, data.stations, setValue])

  useEffect(() => {
    if (data.deperditionMethods) {
      const selectedDeperditionMethod = data.deperditionMethods?.find((item) => item.code === deperditions?.deperditionMethod)
      if (selectedDeperditionMethod) {
        setData((prev) => ({
          ...prev,
          DeperdComponent: selectedDeperditionMethod.component as React.ComponentType<DeperdComponentProps>,
        }))
      }
    }
  }, [data.deperditionMethods, deperditions?.deperditionMethod, deperditions])

  useEffect(() => {
    if (altitude !== undefined && altitude !== null) {
      calculateDeperd()
      getTbase(altitude, seaside)
    }
  }, [altitude])

  const onMitoyenneteChange = useCallback(
    (code?: number, label?: string) => {
      setValue('heatMode.mitoyennete', code, { shouldDirty: true, shouldValidate: true })
      setValue('heatMode.mitoyennete_i18nCode', label)

      calculateDeperd()
    },
    [setValue],
  )

  const onLevelChange = useCallback(
    (code?: number, label?: string) => {
      setValue('heatMode.level', code)
      setValue('heatMode.level_i18nCode', label)

      calculateDeperd()
    },
    [setValue],
  )

  const calculateDeperdAsync = async () => {
    setDisableNavigarion?.(true)
    try {
      const values = getValues('heatMode')
      const hasDetailedZone = values?.zones?.some((z: any) => z.emetteur_i18nCode === EMETTEUR_CODES.DESC_DETAILLEE1 || z.emetteur_i18nCode === EMETTEUR_CODES.DESC_DETAILLEE2)
      const valuesWithoutDetails = {
        ...values,
        zones: values?.zones?.map((z: any) => {
          const { detail, ...rest } = z
          return rest
        }),
      }

      if (deperditions.deperditionMethod === '4') {
        await trigger('heatMode')
      }
      await heatModeSchema.validate(hasDetailedZone ? values : valuesWithoutDetails, { abortEarly: true })
      if (!_.isEmpty(values.deperditions?.deperditionMethod)) {
        const values = getValues()
        dispatch(
          updateForm({
            form: 'heatModeStep',
            data: { form: { ...JSON.parse(JSON.stringify(values.heatMode)) } },
          } as any),
        )

        const newProject = {
          ...projectStore.project,
          forms: {
            heatModeStep: {
              ...projectStore.project.forms.heatModeStep,
              form: JSON.parse(JSON.stringify(values.heatMode)),
            },
          },
        }

        const deperdsResults = await getLoss({
          env: network?.code as string,
          project: newProject,
        }).unwrap()

        const deperd = deperdsResults.Study.StudyDeperdition

        dispatch(setDeperds(deperdsResults))

        if (deperdsResults?.Study?.StudyMaximumTempTransmitter) {
          let maxTemp = zones[0].temperature
          if (zones.length > 1) {
            zones.forEach((z: any) => {
              if (z.temperature > maxTemp) maxTemp = z.temperature
            })
          }
          if (deperdsResults?.Study.StudyMaximumTempTransmitter < parseInt(maxTemp)) {
            dispatch(setNewTransmitterTemp(deperdsResults?.Study.StudyMaximumTempTransmitter))
          } else {
            dispatch(setNewTransmitterTemp(null))
          }
        }
        setDisableNavigarion?.(false)

        return deperd
      }
    } catch (err) {
      // console.error(err)
      console.log(errors)
      dispatch(setDeperds(null))
    }
    setDisableNavigarion?.(false)
  }

  const calcualteFlowTemperatureAsync = useCallback(
    async (deperd: any) => {
      setDisableNavigarion?.(true)
      try {
        const heatModeValues = getValues('heatMode')

        // const isZonesValid = await trigger?.('heatMode.zones')
        await heatModeSchema.validate(heatModeValues, { abortEarly: false })
        // if (!isZonesValid) {
        //   // setDisableNavigarion?.(false)
        //   return
        // }

        for (let index = 0; index < zones.length; index++) {
          const currentZone = heatModeValues.zones[index]

          const isDetailedZone = (currentZone.emetteur_i18nCode === EMETTEUR_CODES.DESC_DETAILLEE1 || currentZone.emetteur_i18nCode === EMETTEUR_CODES.DESC_DETAILLEE2)
            && !_.isEmpty(currentZone.detail)

          if (isDetailedZone) {
            const expand = (valueFn: (d: any) => any) =>
              currentZone.detail.map((d: any) => {
                const arr = []
                for (let i = 1; i <= d.nombreEmeteur; i++) {
                  arr.push(valueFn(d))
                }
                return arr
              })

            const payload = {
              deltaT: 10,
              length: expand((d: any) => parseFloat(d.length)),
              pmax: deperd || 0,
              radiatorClass: expand((d: any) => d.radiatorClass),
              radiatorModel: expand((d: any) => d.radiatorModel),
              setpointTemp: parseInt(heatModeValues.deperditions.temperature),
              width: expand((d: any) => parseFloat(d.width)),
            }

            const res = await getFlowTemp({ data: payload }).unwrap()
            if (res) {
              setValue(`heatMode.zones[${index}].temperature`, res.minTemperature)
            }

            dispatch(mergeFlowTemperature({ index, data: res }))
          }
        }
      } catch (err) {
        console.log(errors)
        console.error(err)
      }
      setDisableNavigarion?.(false)
    },
    [getValues, zones, getFlowTemp, setValue, dispatch, trigger, network, setDisableNavigarion],
  )

  const calculateDeperdAndFlowTemperature = async () => {
    const deperd = await calculateDeperdAsync()


    await calcualteFlowTemperatureAsync(deperd)
  }

  const calculateDeperd = useCallback(() => {
    if (deperditions.deperditionMethod === '4') {
      return
    }
    const hasDetailedZone = zones.some(
      (z: any) =>
        z.emetteur_i18nCode === EMETTEUR_CODES.DESC_DETAILLEE1 ||
        z.emetteur_i18nCode === EMETTEUR_CODES.DESC_DETAILLEE2,
    )

    if (!debounced) {
      debounced = _.debounce(hasDetailedZone ? calculateDeperdAndFlowTemperature : calculateDeperdAsync, 500)
    }

    debounced()
  }, [calculateDeperdAsync, calcualteFlowTemperatureAsync, zones])

  const getTbase = async (altitudeSelectedStation?: number | null, seaside: boolean = false) => {
    if (departement === undefined || altitude === undefined || stationMeteo === undefined) {
      console.warn('Skipping getTbase - missing required fields')
      return
    }

    if (getFieldState('heatMode.departement').isDirty || getFieldState('heatMode.altitude').isDirty || getFieldState('heatMode.stationMeteo').isDirty) {
      try {
        const result = await triggerGetTbase({
          env: network?.code,
          department: departement,
          altitude: altitudeSelectedStation || altitude,
          seaside: seaside ?? heatModeformStore.seaside,
        }).unwrap()

        if (result) {
          const stringResult = String(result)
          dispatch(updateTbase(stringResult))
          setValue('heatMode.tbase', stringResult)
          calculateDeperd()
        }
      } catch (err) {
        console.error('Tbase fetch failed', err)
      }
    }

  }

  const onSeasideChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const seaside = e.target.checked
    setValue('heatMode.seaside', seaside)
    await getTbase(null, seaside)
  }

  const onTbaseChange = async (e: any) => {
    setValue('heatMode.tbase', e?.target?.value || e, { shouldDirty: true, shouldValidate: true })
    setValue('heatMode.tbaseModified', true)

    calculateDeperd()
  }

  const onDeperditionMethodChange = useCallback(
    (code?: string, label?: string, scroll?: any) => {
      if (scroll == null) scroll = true // NOSONAR

      setValue('heatMode.deperditions', {
        ...defaultForm.deperditions,
        temperature: deperditions?.temperature,
      }) // reset deperditions subfields without changing the desired temperature

      onSelect?.('heatMode.deperditions.deperditionMethod', code, label as string) // NOSONAR

      calculateDeperd()

      const selectedDeperditionMethod = data.deperditionMethods?.find((item) => item.code === code)

      if (selectedDeperditionMethod) {
        setData((prev) => ({
          ...prev,
          DeperdComponent: selectedDeperditionMethod.component as React.ComponentType<DeperdComponentProps>,
        }))

      }
    },
    [data.deperditionMethods, deperditions?.temperature, setValue],
  )

  const onSelectDepartments = useCallback(
    (code: string) => {
      const value = data.departments.find((d) => d.code === code)?.label
      onSelect?.('heatMode.departement', code, value as string)
    },
    [data.departments],
  )

  const onSelectStationMeteo = useCallback(
    (code: string) => {
      const value = data.stations.find((d) => d.code === code)?.label
      onSelect?.('heatMode.stationMeteo', code, value as string) // NOSONAR
    },
    [data.stations],
  )

  const addZone = useCallback(() => {
    const currentZones = getValues?.('heatMode.zones')
    if (!currentZones || currentZones.length >= maxHeatModeZones) return
    currentZones?.push(defaultZone)
    setValue('heatMode.zones', currentZones)
  }, [getValues, maxHeatModeZones, setValue])

  const removeZone = useCallback(
    (index: any) => {
      zones?.splice(index, 1)
      setValue('heatMode.zones', zones, { shouldDirty: true })
    },
    [zones, setValue],
  )

  // const handleChange = useCallback((name: string) => (valueOrEvent: any) => {
  //     const value = valueOrEvent?.target?.value ?? valueOrEvent;
  //     setValue(name, value);
  // }, [setValue]);

  return (
    <>
      <group data-length="autofit" data-gap="20">
        <group data-gap="10" data-space="10" data-radius="10" data-direction="column">
          <group data-align="center" data-width="auto" data-gap="10">
            <text data-weight="600" data-text-size="large" data-ellipsis="" data-color="grey-color-dark">
              {t('GEOGRAPHICAL_DATA')}
            </text>
          </group>

          <group data-gap="20">
            <group data-length="forcefit" data-fit="1" data-direction="column" data-size="large">
              <Input
                type={InputType.Select}
                label={'DEPARTMENT'}
                requiredLabel={true}
                control={control}
                name="heatMode.departement"
                options={convertToSelect(data.departments)}
                disabled={!data.departments}
                allowSearch={true}
                size="large"
                placeholder={`${t('SELECT_DEPT_PH')}`}
                loading={isFetchingDepartments}
                onChange={(e: any) => onSelectDepartments(e)}
              //button={isFetchingDepartments ? <SvgLoaderCircleSmall /> : undefined}
              />
            </group>
            <group data-width="forcefit" data-fit="1" data-direction="column" data-size="large">
              <Input
                type={InputType.Select}
                label={'CITY'}
                requiredLabel={true}
                control={control}
                name="heatMode.stationMeteo"
                options={convertToSelect(data.stations)}
                onChange={(e: any) => onSelectStationMeteo(e)}
                disabled={!data.stations || data.stations.length === 0 || isFetchingStations}
                allowSearch={true}
                size="large"
                placeholder={`${t('CITY')}`}
                loading={isFetchingStations}
              />
            </group>
          </group>
        </group>
      </group>

      <group data-length="autofit" data-gap="20">
        <group
          data-gap="10"
          data-space="10"
          //data-background="accent-xtralight"
          data-radius="10"
          data-direction="column"
        >
          <group data-gap="20">
            <group data-length="forcefit" data-fit="1" data-direction="column" data-max-length='275'>
              <Input
                type={InputType.Number}
                label={`${t('ALTITUDE')} (m)`}
                translateLabel={false}
                requiredLabel={true}
                control={control}
                name="heatMode.altitude"
                size="large"
                //placeholder={`${t('ALTITUDE')}`}
                minValue={0}
              // onChange={onAltitudeChange}
              // min={1}
              // max={10}
              />
            </group>

            <group data-length="forcefit" data-fit="1" data-direction="column" data-max-length='275'>
              <Input
                type={InputType.Number}
                label={'BASE_TEMPERATURE'}
                requiredLabel={true}
                control={control}
                name="heatMode.tbase"
                size="large"
                //placeholder={`${t('BASE_TEMPERATURE')}`}
                onChange={onTbaseChange}
                minValue={-50}
                maxValue={50}
                step={1}
              />
            </group>

            {bordDeMer && (
              <group data-width="auto" data-direction="column">
                <group data-space="10" data-gap="10" data-align="center" data-space-top="45">
                  <Checkbox name="heatMode.seaside" label={`${t('SEASIDE')}`} checkbox control={control} onChange={onSeasideChange} />
                  {<group data-width="auto">
                    <Tooltip
                      arrow={false}
                      //color="var(--accent-dark)"
                      color="var(--white)"
                      //space={false}
                      title={
                        <group data-direction="column" data-gap="10" data-space="10" data-max-length="300">
                          <text data-color="black" data-line="1.5">
                            {tHtml('HELP_SEASIDE')}
                          </text>
                        </group>
                      }
                    >
                      {isThermor ?
                        <icon data-svg-size="24" data-svg-color="secondary-light" data-cursor="pointer"><SvgInfoOutline /></icon>
                        :
                        <icon data-svg-size="24" data-svg-color="secondary" data-cursor="pointer"><SvgInfo /></icon>}
                    </Tooltip>
                  </group>}
                </group>
              </group>
            )}
          </group>
        </group>
      </group>

      <group data-length="autofit" data-gap="20">
        <group
          data-gap="10"
          data-space="10"
          //data-background="accent-xtralight"
          data-radius="10"
          data-direction="column"
        >
          <group data-gap="20">
            <group data-length="forcefit" data-fit="1" data-direction="column">
              <Input
                type={InputType.Text}
                label={'PLACE_SAID'}
                control={control}
                name="heatMode.placesaid"
                size="large"
              //placeholder={`${t('PLACE_SAID')}`}
              />
            </group>
          </group>
        </group>
      </group>
      <group data-length="autofit" data-gap="20">
        <group data-gap="10" data-space="10" data-radius="10" data-direction="column">
          <group data-align="center" data-width="auto" data-gap="10">
            <text data-weight="600" data-text-size="large" data-ellipsis="" data-color="grey-color-dark">
              {tHtml('LOSS')}
            </text>
            <LinkButton onClick={(e: any) => {
              e.preventDefault()
              openPopup(
                <HelpSidePopup
                  // client={{} || null}
                  closePopup={closeLastPopup}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: t('HELP_LOSS', { api_url: env.api_url }),
                    }}
                  />
                </HelpSidePopup>,
              )
            }} title={t('HELP_LOSS_INFO')}>
            </LinkButton>
          </group>
          <group data-gap="20">
            <group data-length="autofit" data-fit="1" data-direction="column" data-size="large">
              <Input
                type={InputType.Select}
                control={control}
                name="heatMode.deperditions.temperature"
                label={`${t('DESIRED_TEMP')} (°C)`}
                translateLabel={false}
                options={convertToSelect(temperature)}
                allowSearch={true}
                size="large"
                dataMaxLength='350'
              //placeholder={`${t('DESIRED_TEMP')} (°C)`}
              />
            </group>
          </group>
          <group data-gap="10" data-space="10" data-space-left="0" data-radius="10" data-direction="column">
            <group data-width="auto" data-direction="column" data-justify="end">
              <group data-gap="20" data-align="center" data-name="custom_option">
                <CustomSelector
                  itemLength="180"
                  name="heatMode.deperditions.deperditionMethod"
                  control={control}
                  items={data.deperditionMethods}
                  selected={deperditions?.deperditionMethod}
                  onChange={onDeperditionMethodChange}
                />
              </group>
            </group>
          </group>

          {data.DeperdComponent && deperditions?.deperditionMethod && (
            <data.DeperdComponent
              deperditions={deperditions}
              control={control}
              errors={errors}
              touched={''}
              onChange={() => { }}
              onBlur={() => { }}
              calculateDeperd={calculateDeperd}
              watch={watch}
              setValue={setValue}
              getFieldState={getFieldState}
              onSelect={onSelect}
              addZone={addZone}
              removeZone={removeZone}
              getValues={getValues}
              setDisableNavigarion={setDisableNavigarion}
              trigger={trigger}
              calculateDeperdAndFlowTemperature={calculateDeperdAndFlowTemperature}

            />
          )}
        </group>
      </group>

      {deperditions?.deperditionMethod && deperditions?.deperditionMethod != 4 && <>
        <group data-length="autofit" data-gap="20">
          <group
            data-gap="10"
            data-space="10"
            //data-background="accent-xtralight"
            data-radius="10"
            data-direction="column"
          >
            <group data-align="center" data-width="auto" data-gap="10">
              <text data-weight="600" data-text-size="large" data-ellipsis="" data-color="grey-color-dark">
                {t('HABITAT_DATA')}
              </text>
            </group>
            <group data-length="autofit" data-fit="1" data-direction="column" data-size="large" data-gap="10" >
              <Input
                type={InputType.Select}
                label={'HOUSE_TYPE'}
                requiredLabel={true}
                control={control}
                options={houseTypes}
                name={`heatMode.deperditions.houseType`}
                allowSearch={true}
                size="large"
                translateValue={true}
                defaultValue={houseTypes[0].value}
              />
            </group>
            <group data-width="auto" data-direction="column" data-justify="end">
              <label className="form_fields" data-space-bottom="10">
                <div className="data_label">
                  {t('JOINT_OWNERSHIP')}
                  <i data-space-horizontal="5" data-color="red">
                    *
                  </i>
                </div>
              </label>

              <group data-gap="20" data-align="center" data-name="custom_option">
                <CustomSelector
                  name="mitoyennete"
                  control={control}
                  items={MITOYENNETE_ITEMS}
                  selected={mitoyennete}
                  onChange={onMitoyenneteChange}
                />
              </group>
            </group>

            <group data-width="auto" data-direction="column" data-justify="end">
              <label className="form_fields" data-space-bottom="10">
                <div className="data_label">
                  {t('HABITAT_TYPE')}
                  <i data-space-horizontal="5" data-color="red">
                    *
                  </i>
                </div>
              </label>

              <group data-gap="20" data-align="center" data-name="custom_option">
                <CustomSelector name="level" control={control} items={LEVEL_ITEMS} selected={level} onChange={onLevelChange} />
              </group>
            </group>
          </group>
        </group>

        <group data-length="autofit" data-gap="20">
          <group data-gap="10" data-space="10" data-radius="10" data-direction="column">
            <group data-align="center" data-width="auto" data-gap="10">
              <text data-weight="600" data-text-size="large" data-ellipsis="" data-color="grey-color-dark">
                {t('HABITAT_DESCRIPTION')}
              </text>
              {<group data-width="auto">
                <Tooltip
                  arrow={false}
                  //color="var(--accent-dark)"
                  color="var(--white)"
                  //space={false}
                  title={
                    <group data-direction="column" data-gap="10" data-space="10" data-max-length="300">
                      <text data-color="black" data-line="1.5">
                        {tHtml('HELP_DESC_HABITAT')}
                      </text>
                    </group>
                  }
                >
                  {isThermor ?
                    <icon data-svg-size="24" data-svg-color="secondary-light" data-cursor="pointer"><SvgInfoOutline /></icon>
                    :
                    <icon data-svg-size="24" data-svg-color="secondary" data-cursor="pointer"><SvgInfo /></icon>}
                </Tooltip>
              </group>}
            </group>

            {zones?.slice(0, maxHeatModeZones).map((zone: any, index: any) => (
              <HeatModeZone
                key={index}
                name={`zones[${index}]`}
                control={control}
                index={index}
                zone={zone}
                setValue={setValue}
                addZone={addZone}
                removeZone={removeZone}
                maxZones={maxHeatModeZones}
                //errors={errors}
                //touched={touched}
                calculateDeperd={calculateDeperd}
                onSelect={onSelect}
                getValues={getValues}
                setDisableNavigarion={setDisableNavigarion}
                calculateDeperdAsync={calculateDeperdAsync}
              />
            ))}
          </group>
        </group>
      </>
      }

      <HeatModeCalculatedNeeds
        deperditionCode={deperditions?.deperditionMethod}
        calculateDeperdAndFlowTemperature={calculateDeperdAndFlowTemperature}
        trigger={trigger}
      />
    </>
  )
}

export default HeatMode
